/*     
* A Chatroom should not:                
. render a chat widget's  look n feel 
. define how & when chat widget UI should behave in a certain manner
. be responsible to render & handle any other widget type like Visitor Form and Video Chat
    
* A Chatroom should:
. handle visitor response
. handle chat streaming events
. maintain list of chat log/history
         
*/
import BaseClass from './BaseClass';
import WidgetTypes from './enums/WidgetTypes';
import TempCache from './enums/TempCache';
import ParticipantIds from './enums/ParticipantIds';
import GlobalVariables from './GlobalVariables';
import UserIds from './hooks/UserIds';
import ChatMode from './hooks/ChatMode';
import PipedMessageHandler from './hooks/PipedMessageHandler';
import Queue from './hooks/Queue';
import { ScriptsQuestionType, SystemMessageType } from './enums/Enum';
import sendChatAndLeadAnalytics from './hooks/sendChatAndLeadAnalytics';
import canBrowserDoLive from './hooks/canBrowserDoLive';
import RunAsSoonAs from './hooks/RunAsSoonAs';
import ChatStates from './hooks/ChatStates';
import Cookies from './hooks/Cookies';
import ChatEvents from './hooks/ChatEvents';
import WidgetStates from './hooks/WidgetStates';
import getCookieValue from './hooks/getCookieValue';
import getStorageAPIValue from './hooks/getStorageAPIValue';
import fromQueryString from './hooks/fromQueryString';
import storageAPIExists from './hooks/storageAPIExists';
import logIt from '../../logIt';
import isAutoLaunchChatWindow from '../isAutoLaunchChatWindow';

export default class LiveChatRoom extends BaseClass {
    constructor(options) {

        const objectType = WidgetTypes.ChatRoom;
        const thisOptions = Object.assign(options, {
            widget: objectType,
            $: options.$
        });
        super(thisOptions);
        this.widget = objectType;
        const me = this;
        me.isNewChat = typeof ApexChatStartData !== 'undefined';
        me.chatId = options.chatId;
        me.suppressHistory = options.isFromInvitation;
        // messageSequence keeps track of the sequence # of message from visitor - this is a Lumin request
        this.messageSequence = 0;
        this.subscribe(ChatStates.MessageArrived, (m) => {
            logIt(`after::LiveChatRoom::${ChatStates.MessageArrived}:${m.text}`);
            if (!m.isFromHistory) {
                // this guy only deals in History fetching scenarios
                return;
            }
            logIt(
                `after::LiveChatRoom::${ChatStates.MessageArrived}: loading history items:${m.text}`
            );

            if (me.Queue && me.Queue.getLength() > 0) {
                // logIt('after::reload::' + ChatStates.MessageArrived + ' m done:' + m.text + '\n' + JSON.stringify(m));
                // before rendering Last message need to publish that chat is rendered and take decision on disabling TextArea for Disabling.
                if (me.Queue.getLength() === 1) {
                    me.postMessage({ event: ChatEvents.OnHistoryLoadedCompletely });
                }
                const msg = me.dequeueMessage();
                logIt(
                    `${me.Queue.getLength()} remaining::after::reload:: dequeueing msgs:${msg.text}`
                );
                logIt(`after::${JSON.stringify(me.Queue.getItems())}`);
                me.onMessageArrived(msg, true, msg.index);
            }
        });
    }

    end() { }

    setWebSyncClient() {
        if (!ApexChat.webSyncClient) {
            ApexChat.webSyncClient = new fm.websync.client(`${GlobalVariables.LiveChatServiceUrl}request.ashx`);
            fm.util.ready();
            ApexChat.webSyncClient.connect({
                requestUrl: `${GlobalVariables.LiveChatServiceUrl}request.ashx`,
                stream: {
                    requestUrl: `${GlobalVariables.LiveChatServiceUrl || GlobalVariables.LiveChatServiceUrl}request.ashx`
                },
                // never auto-disconnect; this gives the visitor a moment to refresh the browser, lose a network connection, etc
                autoDisconnect: false,
                // ignore if we're already initialized
                quiet: true,
                stayConnected: true,
                onSuccess(args) { },
                onFailure(args) {
                    args.reconnect = true;
                },
                onStreamFailure(args) {
                    // stream is dead...we're going to retry, so for now we'll just leave this
                }
            });
        }

        this.webSyncClient = ApexChat.webSyncClient;
    }

    getMeta(chatId, companyId, token) {
        return {
            chatId,
            companyId,
            token
        };
    }

    /**
     * Its subscribing to Chat channel, so, this minimal connection, especially, in case of Refresh when no DOM & no websync.
     * @param {id} id is basically ChatID
     */
    subscribeToChatChannel(id) {
        const me = this;
        me.options.chatId = id;
        const onSuccess = (args) => {
            let meta = args.getMeta();
            const isPreChatMaximized =
                me.state.getStateValue(WidgetTypes.PreChat) === WidgetStates.ShownMaximized;
            me.options.participantId = meta.participantId;
            // Only need to send Last Message of History not all.
            if (isPreChatMaximized && meta.history) {
                // check prechat is not maximize
                const indexOfLastMessage = meta.history.length - 1;
                meta.history[indexOfLastMessage] &&
                    me.onMessageArrived(meta.history[indexOfLastMessage], true, indexOfLastMessage);
            }
        };
        const channels = [
            `/chatroom/${id}`,
            `/chatroom/${id}/typing`,
            `/chatroom/${id}/participants`,
            `/chat/${id}/transferRequested`,
            `/chatroom/${id}/messagereceipt/`
        ];
        try {
            if (fm) {
                me.setWebSyncClient();
            }
            const meta = me.getMeta(id, me.options.companyId, me.options.token);
            me.webSyncClient.subscribe({
                retries: 3,
                meta,
                channels,
                onSuccess,
                onReceive(args) {
                    const data = args.getData();
                    /* return if message coming from predefined */
                    if (data.userId === 0 || data.participantId === ParticipantIds.predefined) {
                        return;
                    }
                    me.onMessageArrived(data);
                },
                onFailure(args) {
                    me.onChatJoinFailure(args);
                }
            });
        } catch (error) {
            console.log("fm not defined::", error);
        }
    }

    processHistoryItems(meta, isChatActive, cb) {
        const me = this;
        ApexChat.allHistoryMessages = ApexChat.allHistoryMessages || []; // store all messages
        for (var i = 0; i < meta.history.length; i++) {
            let match = false; // check for history messages already appeared.
            // CHAT-7391 this scenario is for when we moved our application to background and return to application
            // check if message already appeared on dom we should not run it again to avoid duplication
            match = ApexChat.allHistoryMessages.some(
                (msg) => meta.history[i].id === msg.id // if msg id from meta history matches with our allhistorymessages id it should not append to DOM.
            );
            if (!match) {
                // If chat is being loaded from history, we need to set message sequece separately for chat handlers as well as visitors
                const setMessageSequenceFromHistory = function (log, currentSequence) {
                    // System message
                    if (!log.messageSequence) {
                        return currentSequence;
                    }
                    // is agent or operator?
                    const isAgentOrOperator =
                        ApexChat.Settings && ApexChat.Settings.CurrentUserId > 0;
                    // we maintain 2 separate sequences, on for agent and one for visitor
                    // bail out if not relevant
                    if (
                        isAgentOrOperator &&
                        (log.userId <= 0 || log.userId !== ApexChat.Settings.CurrentUserId)
                    ) {
                        return currentSequence;
                    }
                    if (
                        (!isAgentOrOperator &&
                            log.messageSequence &&
                            log.userId == 0 &&
                            log.participantId > 0) ||
                        isAgentOrOperator
                    ) {
                        // extract last 2 characters as they reperesent message sequence
                        const messageSequence = Number(log.messageSequence.substring(8, 10));
                        // we need the current sequence regardless of the order of messages from the server
                        if (currentSequence < messageSequence) {
                            currentSequence = messageSequence;
                        }
                    }
                    return currentSequence;
                };
                // we dont need to run history messages again if its already appeared on DOM.
                // when loading history, we filter out only those messages which the visitor sends
                me.messageSequence = setMessageSequenceFromHistory(
                    meta.history[i],
                    me.messageSequence
                );
                // Bug_7343 - Visitor's responses against FullText not appearing
                // no matter what questionType message is belongs to the function itself will handle, won't work for QTs except FullText
                meta.history[i].options = me.handleFullTextVisitorResponse(meta.history[i]);
                meta.history[i].options = me.handleVisitorResponsesRenderAsText(meta.history[i]);
                meta.history[i].index = i;
                logIt('Adding history messages');
                // logIt(JSON.stringify(meta.history[i]));
                me.queueMessage(meta.history[i]); // just cache history items, we will handle them manually below
                ApexChat.allHistoryMessages.push(meta.history[i]);
            }
        }
        // we process 1st history item in refresh scenario ourself
        if (me.Queue && me.Queue.getLength() > 0 && !me.isChatEnded) {
            logIt('Queue messages');
            logIt(JSON.stringify(me.Queue.getItems()));
            const msg = me.dequeueMessage();
            me.onMessageArrived(msg, isChatActive, msg.index);
        }
        if (cb && typeof cb === 'function') {
            cb();
        }
    }

    setChatId(id, token) {
        const me = this;
        me.options.chatId = id;
        me.options.token = token;
        let onSuccess = function (args) {
            let meta = args.getMeta();
            me.options.participantId = meta.participantId;
            if (me.isPageRefreshed()) {
                me.postMessage({ event: ChatEvents.OnHistoryStartLoading });
            }
            me.isConnectionEstablished = true;
            // If chat is new our initial message's inital load should be false, if chatmode is windowed we need to get chat status from querystring.
            const isChatActive =
                me.isChatActive() ||
                (me.options.chatMode === ChatMode.Windowed &&
                    ApexChat.TryParse(fromQueryString('isChatActive')));
            me.processHistoryItems(meta, isChatActive, () => {
                me.onChatJoined(args);
                ApexChat.storageAPI.Store(TempCache.CHAT_ID_COOKIE, id);
            });
        };
        if (me.suppressHistory) {
            onSuccess = function (args) {
                me.updateBubbleMsgCount(args);
                logIt('bypassing displaying history...');
                logIt(args);
            };
        }
        const channels = [
            `/chatroom/${id}`,
            `/chatroom/${id}/typing`,
            `/chatroom/${id}/participants`,
            `/chat/${id}/transferRequested`,
            `/chatroom/${id}/messagereceipt/`
        ];
        if (fm) {
            me.setWebSyncClient();
        }
        let meta = me.getMeta(id, me.options.companyId, me.options.token);
        me.webSyncClient.subscribe({
            retries: 3,
            meta,
            channels,
            onSuccess,
            onReceive(args) {
                const data = args.getData();
                switch (args.getChannel()) {
                    case channels[0]:
                        // no need to take action if message is coming from visitor
                        if (data.userId == 0) {
                            return;
                        }
                        // return if message coming from predefined
                        if (
                            data.participantId == ParticipantIds.predefined ||
                            data.isPredefinedPokeMessage
                        ) {
                            return;
                        }
                        // Simple null check for allHistoryMessages before Pushing.
                        ApexChat.allHistoryMessages = ApexChat.allHistoryMessages || [];
                        var lastDOMState = me.state.getStateValue(WidgetTypes.DOM); // get last state of widget
                        // we need only run this maximize case; not the RD/DOM is minimized
                        if (!(lastDOMState == WidgetStates.ShownMinimized)) {
                            ApexChat.allHistoryMessages.push(data);
                        }
                        if (
                            me.state.getStateValue(WidgetTypes.PreChat) ===
                            WidgetStates.ShownMaximized
                        ) {
                            ApexChat.allHistoryMessages.push(data);
                        }
                        if (
                            me.isWindowedStarted() ||
                            (me.options.chatMode &&
                                me.options.chatMode == ChatMode.Windowed)
                        ) {
                            ApexChat.allHistoryMessages.push(data);
                        }
                        if (data.systemMessageType == SystemMessageType.WebToSMSTransferCompleted) {
                            me.onMessageArrived(data);
                            me.postMessage({ event: WidgetTypes.ChatRoom + ChatEvents.ChatIdleOut });
                            me.$('#apexchat .switchto-text-chat', me.options.context).remove();
                            me.$('#apexchat #menuItemPopout', me.options.context).remove();
                        }
                        // handle SMS transfer system messages for RD and non-RD
                        else if (
                            data.systemMessageType ==
                            SystemMessageType.SMSTransferInProgress ||
                            data.systemMessageType == SystemMessageType.SMSTransferred
                        ) {
                            me.OnSMSTransfer(data);

                        } else {
                            me.onMessageArrived(data);
                        }
                        break;
                    case channels[1]:
                        if (me.options.participantId === data.participantId) {
                            return;
                        }
                        me.onTypingArrived(data);
                        break;
                    case channels[2]:
                        me.onParticipantsChanged(data);
                        break;
                    case channels[3]:
                        // me.OnTransferRequested(data);
                        break;
                    case channels[6]: // templated questions
                        break;
                    case channels[8]: // realtime-text
                        break;
                    default:
                }
            },
            onFailure(args) {
                meta = args.getMeta();
                if (meta && meta.history) {
                    logIt('OnFailure-Messages');
                    // logIt(JSON.stringify(meta.history));
                    // for (var i = 0; i < meta.history.length; i++) {
                    //    me.onMessageArrived(meta.history[i], true, i);
                    // }
                    const isChatActive =
                        me.isChatActive() ||
                        (me.options.chatMode === ChatMode.Windowed &&
                            ApexChat.TryParse(fromQueryString('isChatActive')));
                    me.processHistoryItems(meta, isChatActive);
                }
                me.onChatJoinFailure(args);
            },
            onComplete(args) { }
        });
    }

    updateBubbleMsgCount(args) {
        let counterMessage = 0;
        let lastMessageId = 0;
        let msgCountUpdated = false;
        const me = this;
        if (storageAPIExists('livechat_new_msg_count')) {
            counterMessage = parseInt(getStorageAPIValue('livechat_new_msg_count'));
        }
        if (storageAPIExists('livechat_msg_count_updated')) {
            msgCountUpdated = getStorageAPIValue('livechat_msg_count_updated');
        }
        if (storageAPIExists('livechat_last_msg_id')) {
            lastMessageId = parseInt(getStorageAPIValue('livechat_last_msg_id'));
        }
        const agentMsgsCount =
            args.getMeta().history != null
                ? args.getMeta().history.filter((m) => m.userId > 0 && m.id > lastMessageId)
                : [];
        if (msgCountUpdated === 'true') {
            const noOfNewAgentMsgs = agentMsgsCount.length + counterMessage;
            ApexChat.storageAPI.StoreTemp('livechat_new_msg_count', noOfNewAgentMsgs);
            ApexChat.storageAPI.StoreTemp('livechat_msg_count_updated', false);
        }
        me.postMessage({ event: ChatEvents.OnHistoryLoadedCompletely });
    }

    setNewChat(isNewChat) {
        this.options.isNewChat = isNewChat;
    }

    initChatRoom() {
        this.state.changeState({ toState: ChatStates.Requested });
        const me = this;
        if (typeof fm !== 'undefined') {
            me.setWebSyncClient();
        } else {
            console.log('WebSync is not loaded');
        }
    }

    sendVisitorRealtimeTyping(text, clear) {
        const me = this;
        me.webSyncClient.publish({
            channel: `/chatroom/${me.options.chatId}/realtime/text`,
            data: {
                text,
                clear
            },
            onSuccess(args) {
                // me.onVisitorRealtimeTypingArrived(args.data);
            },
            onFailure(args) { }
        });
    }

    onChatJoined(args) {
        const me = this;
        me.isLead = false;
        // Extracting value of visitor client Id from query string.
        const visitorsGoogleClientId = Cookies.get('ga_client_id');
        if (me.isNewChat) {
            // Sending data over to analytics for chats
            sendChatAndLeadAnalytics(ApexChatStartData, true, visitorsGoogleClientId);
        } else {
            // clean out the real time response area (Refresh scenario)
            me.sendVisitorRealtimeTyping('', true);
        }
        // subscribing the channel which informs that chat becomes lead; now sending leads data to analytics if any analytics is available
        me.webSyncClient.subscribe({
            retries: 3,
            channel: `/chatroom/${me.getChatId()}/meta`,
            onSuccess() { },
            onReceive(args) {
                const data = args.getData();
                if (me.isLead) {
                    return;
                }
                if (data.isLead) {
                    me.isLead = true;
                    sendChatAndLeadAnalytics(ApexChatStartData, false, visitorsGoogleClientId);
                }
            },
            onFailure() { }
        });
        const data = {
            messageIds:
                getCookieValue(TempCache.APEXCHAT_PRECHAT_MESSAGE_IDS_COOKIE) ||
                fromQueryString('prechatids'),
            agentName: me.options.agentName
        };
        if (args.participantDisplayName) {
            data.participantDisplayName = args.participantDisplayName;
        }
        if (me.options.chatId == 0 || me.options.chatId == undefined) {
            me.options.chatId = me.chatId;
        }
        const publishData = { data, meta: me.getMeta(), chatId: me.options.chatId };
        // CHAT-8201 Windowed chat not initiated on beta when start from prechat
        // check if prechat messages ids not exists and query string has prechatIds so defined cookie on windowed mode
        // previously it was not creating a cookie due to different domain on beta, so we need to create it from querystring
        if (
            !storageAPIExists(TempCache.APEXCHAT_PRECHAT_MESSAGE_IDS_COOKIE) &&
            me.options.chatMode === ChatMode.Windowed
        ) {
            const prechatIds = fromQueryString('prechatids');
            if (prechatIds) {
                ApexChat.storageAPI.Store(
                    TempCache.APEXCHAT_PRECHAT_MESSAGE_IDS_COOKIE,
                    prechatIds
                );
            }
        }
        /* CHAT-6280-Handle PreChat chat joined hits
         * invoked event here so specfic widget(prechat or other widget) can publish whenever required
         *
         For Windowed Chat, had to move publishPreChatMessages From PreChat Class to ChatRoom Class
         Added Strong conditions to make sure that IF PreChat Messages has sent already than no need to send it again (CHAT-6643 FIX) */
        if (
            (storageAPIExists(TempCache.APEXCHAT_PRECHAT_MESSAGE_IDS_COOKIE) || fromQueryString('prechatids')) &&
            !storageAPIExists(TempCache.APEXCHAT_PRECHAT_MESSAGE_SENT_COOKIE)
        ) {
            console.log('publishPreChatMessages')
            me.publishPreChatMessages(publishData);
        } else {
            console.log('publishPreChatMessages not called');
        }
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnChatJoined,
            value: publishData
        });
    }

    onChatJoinFailure(args) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnChatJoinFailure,
            value: args
        });
    }

    sendLive() {
        const me = this;
        if (this.options.isLiveAvailable && this.firstMessageArrived) {
            this.webSyncClient.publish({
                retries: 3,
                channel: `/chatroom/${me.getChatId()}/meta`,
                data: {
                    live: canBrowserDoLive()
                }
            });
            this.sendLive = function () { }; // do it once and only once
        }
    }

    startBatch() {
        this.webSyncClient.startBatch();
    }

    endBatch() {
        this.webSyncClient.endBatch();
    }

    sendMessage(args) {
        const me = this;
        if (me.isChatTransferredToSMS) {
            me.isChatTransferredToSMS = false;
            me.state.changeState({
                origin: me.objectType,
                toState: ChatEvents.OnSendMessageFailure,
                value: {}
            });
        }
        if (window.ApexChatStartData) {
            Object.assign(me.options, ApexChatStartData);
        }
        // avoid sending blank text
        // CHAT-7539 - ChatId before sending responses.
        if (
            !me.options.chatId ||
            !me.getChatId() ||
            !args.chatMsg ||
            (args.chatMsg && args.chatMsg.replace(/\s/g, '') === '')
        ) {
            // CHAT-8877 - DarkOverlay was not appearing If Chat Started and page get refreshed and session gets ended
            // because ChatId we are making null and it was returning and WebSync OnFailure was not executing
            me.state.changeState({
                origin: me.objectType,
                toState: ChatEvents.OnSendMessageFailure,
                value: null
            });
            return;
        }
        me.webSyncClient.publish({
            retries: 3,
            channel: `/chatroom/${me.getChatId()}/meta`,
            data: {
                live: canBrowserDoLive()
            }
        });
        const data = {};
        data.text = args.chatMsg;
        data.isForAIBot = args.isForAIBot;
        data.questionTypeChatLogIdToBeUpdated = args.questionTypeChatLogIdToBeUpdated;
        data.participantId = args.agentParticipantId || me.options.participantId;
        data.isPredefinedResponse = args.isPredefinedResponse;
        data.timeCalendarOrText = args.timeCalendarOrText;
        data.hasVisitorAttachments = args.hasVisitorAttachments;
        if (args.isGreeting) {
            data.isGreeting = args.isGreeting;
        }
        if (args.contactInfo) {
            // Contact info json
            data.contactInfo = args.contactInfo;
        }
        // If this script text has associated agent script answer details
        if (args.answerScript) {
            // Set these properties here, to pass these details in ChatLog table
            data.scriptAnswerId = args.answerScript.agentScriptAnswerId;
            data.questionType = args.answerScript.questionType;
            data.options = args.answerScript.options;
            // If message is predefind poke message
            if (
                args.agentParticipantId === ParticipantIds.predefined &&
                args.answerScript.agentParticipantId === ParticipantIds.pokeMessage
            ) {
                data.participantId = args.answerScript.agentParticipantId;
                data.isPredefinedPokeMessage = true;
            }
        }

        // Send QT response flag with the data
        data.isQTResponseSent = args.questionType > 0;

        if (args.participantDisplayName) {
            data.participantDisplayName = args.participantDisplayName;
        }
        if (args.userId) {
            // sending userId as a data in webSyncClient publish so it can update on db according to message type(agent,predefined or visitor)
            data.userId = args.userId;
        }
        const argsContext = args;
        // We are assigning message sequence for each new message here
        const formatMessageSequence = (number) => {
            // is agent or operator?
            var isAgentOrOperator = ApexChat.Settings && ApexChat.Settings.CurrentUserId > 0;
            // the format of message sequence is userId (8 digits) + sequence number of message
            // for visitor UserId is always 0
            var prefix = '00000000';
            // we maintain two separate sequences for agents/operators and visitors
            if (isAgentOrOperator) {
                var userIdLen = ApexChat.Settings.CurrentUserId.toString().length;
                // add as many 0s as needed to  make it 8 digits long
                prefix = prefix.slice(0, prefix.length - userIdLen) + ApexChat.Settings.CurrentUserId;
            }
            // limit to two digits (edge case), just in case its a long chat- which has never happned
            if (number > 99) {
                number = number % 100;
            }
            var messageSequenceFormatted = number;
            // if number is less than 10, add a leading 0, to make it a double digit string
            if (number < 10) {
                messageSequenceFormatted = '0' + number;
            }
            return prefix + messageSequenceFormatted;
        };
        if (data.participantId > 0) {
            this.messageSequence++;
            data.messageSequence = formatMessageSequence(this.messageSequence);
        }
        if (args.agentParticipantId == ParticipantIds.predefined) {
            data.delayTime = args.delayTime;
        }
        data.isUnread = true;
        me.webSyncClient.publish({
            retries: 0,
            meta: me.getMeta(me.options.chatId, me.options.companyId, me.options.token),
            channel: `/chatroom/${me.options.chatId}`,
            data,
            onSuccess(args) {
                /* server sending invalid text of predefined msg with nested html code and tag, replacing server's predefined msg with clients chat msg */
                const getData = args.getData();
                getData.userId = getData.userId || ParticipantIds.visitor;
                getData.text = argsContext.chatMsg;
                Cookies.set('livechat_chat_lastmessage', JSON.stringify(getData)); // CHAT-7300 set last message on cookies
                if (argsContext.shouldRenderResponseAsText) {
                    // in case of a full text response, we forcefully clear it from being tagged: QuestionTypeAnswer so that we can show the full text response on chat messages
                    getData.options = null;
                }
                if (getData.userId == ParticipantIds.visitor) {
                    // message send by visitor should also be part of allHistoryMessages.
                    ApexChat.allHistoryMessages.push(getData);
                }
                if (argsContext.onSuccess) {
                    argsContext.onSuccess(getData);
                }
                if (argsContext.agentParticipantId != ParticipantIds.predefined) {
                    // predefined messages should not be shown after publish predefined messages, since they are already shown before
                    me.state.changeState({
                        origin: me.objectType,
                        toState: ChatEvents.OnSendMessageSuccess,
                        value: args.getData ? getData : args
                    });
                }
            },
            onFailure(args) {
                me.state.changeState({
                    origin: me.objectType,
                    toState: ChatEvents.OnSendMessageFailure,
                    value: args
                }); // passing error object as value is more informative than passing just an error message
            }
        });

        if (args.callback) {
            args.callback();
        }
    }

    sendTyping(indicator, text) {
        const operatorName =
            ApexChat && ApexChat.Settings ? ApexChat.Settings.CurrentUserDisplayName : '';
        const me = this;
        if (!text) {
            text = '';
        }
        me.webSyncClient.publish({
            channel: `/chatroom/${me.getChatId()}/typing`,
            data: {
                participantId: me.options.participantId,
                // Visitor Name (available on Visitor End) || Agent/Operator Aliase Name || Operator Name if aliase not selected
                participantDisplayName:
                    me.options.participantDisplayName ||
                    me.agentAlias ||
                    operatorName ||
                    'Chat Representative',
                indicator,
                text
            },
            onSuccess(args) {
                me.onTypingArrived(args.getData(), true);
            },
            onFailure(args) {
                if (window.console) {
                    window.console.log(args.error);
                }
            }
        });
    }

    onMessageTyping(e) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnMessageTyping,
            value: e
        });
    }

    updateChatOnHoldStatus(chatId) {
        fm.network.xhr({
            retries: 0,
            method: 'POST',
            url: `/Services/ApexChatService.svc/updatechatonholdstatus/${chatId}/false`,
            data: {},
            onSuccess() {
                try {
                    $('#videoChatStatus').hide();
                    $('.apexchat-live-status-title').remove('');
                    // ApexChat.Invitation.showInvitation();
                } catch (e) { }
            },
            onFailure(args) { }
        });
    }

    queueMessage(m) {
        const me = this;
        me.Queue = me.Queue || new Queue();
        me.Queue.enqueue(m);
    }

    dequeueMessage() {
        const me = this;
        if (me.Queue.getLength() == 0) {
            return null;
        }
        const popped = me.Queue.dequeue();
        return popped;
    }

    onMessageArrived(m, initialLoad, index) {
        const me = this;
        m.isFromHistory = initialLoad;
        const delayPromise = me.delayOnMessageArrived(m);
        Promise.all([delayPromise]).then(() => {
            const args = { m, initialLoad, index };
            if (m.isTranslating) {
                return;
            }
            const isNotSystemMessage = m.participantId > ParticipantIds.visitor &&
                m.questionType != ScriptsQuestionType.MultipleChoiceConsent &&
                m.questionType != ScriptsQuestionType.WebToSMSNotification
                && m.questionType != ScriptsQuestionType.PhoneOTPDialog;
            // persist input hidden on every message except agent message from dashboard
            if (isNotSystemMessage) {
                const chatContentWrap = me.options.context.querySelector('.apexchat_hide_msg_input');
                if (chatContentWrap) {
                    chatContentWrap.classList.remove('apexchat_hide_msg_input');
                }
            }
            logIt(`chatroom-v3::onMessageArrived:${args.m.text}`);

            logIt(`validating if msg is piped:${JSON.stringify(args.m)}`);
            if (m.isChatEnded) {
                const PipedMessageHandlerSingleton = new PipedMessageHandler();
                // is it chat ended system msg? hold on in case a piped msg is being processed
                me.isChatEnded = true;
                RunAsSoonAs(
                    () => {
                        logIt('safely end chat:');
                        me.postMessage({
                            event: WidgetTypes.PokeMessagesOverLay + WidgetStates.Hidden
                        });
                        me.postMessage({
                            event: WidgetTypes.ChatRoom + ChatEvents.ChatIdleOut
                        });
                        me.state.changeState({
                            origin: me.objectType,
                            toState: ChatEvents.OnMessageArrived,
                            value: args
                        });
                    },
                    () => PipedMessageHandlerSingleton.isProcessing() == false
                );
            } else {
                const isFromHistory = initialLoad;
                // There are few scenarios in which we need to manipulate the typing indication time.
                // 1. In case of Refresh the history message should show without typing & with no time delay.
                // 2. If the message has been shown on PreChat so after moving to DOM the there should be no extra delay added.
                const messageInterval = isFromHistory || m.isFromPreChat ? 2 : me.options.questionTypeDelay * 1000; // set default interval
                // process a piped msg or a normal/non-piped msg
                if (isFromHistory) {
                    if (args.m.questionType == ScriptsQuestionType.AppointmentScheduler) {
                        // Set the options for appointment scheduler
                        if (!args.m.questionTypeSettings && args.m.options) {
                            args.m.questionTypeSettings = args.m.options;
                        }
                    }
                }
                const PipedMessageHandlerSingleton = new PipedMessageHandler();
                if (PipedMessageHandlerSingleton.isValidPipedMessage(args)) {
                    logIt(`QUEUED:processing piped${args.m.text}`);

                    PipedMessageHandlerSingleton.startPolling({
                        init() {
                            me.state.changeState({
                                origin: me.objectType,
                                toState: ChatEvents.OnTypingArrived,
                                value: { indicator: true }
                            });
                        },
                        async startPollingCallback(m) {
                            logIt(`QUEUED:startPollingCallback:${m.text}`);

                            me.state.changeState({
                                origin: me.objectType,
                                toState: ChatEvents.OnMessageArrived,
                                value: { m, initialLoad, index }
                            });
                            me.state.changeState({
                                origin: me.objectType,
                                toState: ChatEvents.OnTypingArrived,
                                value: { indicator: true }
                            });
                        },
                        lastPollingCallback(args) {
                            logIt(`QUEUED:lastPollingCallback:${args.m.text}`);

                            me.state.changeState({
                                origin: me.objectType,
                                toState: ChatEvents.OnTypingArrived,
                                value: { indicator: false }
                            });
                            me.state.changeState({
                                origin: me.objectType,
                                toState: ChatEvents.OnMessageArrived,
                                value: args
                            });
                        },
                        messageDisplayDuration: messageInterval,
                        isFromHistory
                    });
                } else {
                    me.state.changeState({
                        origin: me.objectType,
                        toState: ChatEvents.OnMessageArrived,
                        value: args
                    });
                }
                ApexChat.addDelayToMappedQuestion = m.isNextQuestionMapped;
            }
            Cookies.set('livechat_chat_lastmessage', JSON.stringify(m)); // CHAT-7300 set last message on cookies
            if (m.userId > 0) {
                // message from the agent - stop waiting timer
                me.state.changeState({
                    origin: me.objectType,
                    toState: ChatEvents.OnStopWaiting,
                    value: args
                });
            }
        });
    }

    /** add delay to question type as per text length
     * If the length is greater than 100 the default timeout apply
     * other than that it will calculate the time by multiplying text length with 150
     */
    delayOnMessageArrived(message) {
        const me = this;
        return new Promise((resolve) => {
            let timeout = 0;
            ApexChat.options = me.options || '';
            const PipedMessageHandlerSingleton = new PipedMessageHandler();
            if (
                message.text &&
                message.questionType > ScriptsQuestionType.None &&
                !PipedMessageHandlerSingleton.isValidPipedMessage({ m: message }) &&
                !message.isFromHistory
            ) {
                const messageText = message.text.replace(/<[^>]*>?/gm, '');
                if (ApexChat.addDelayToMappedQuestion && ApexChat.VisitorResponse) {
                    function estimator(number) {
                        if (number <= 30) {
                            return 2000;
                        }
                        if (number <= 50) {
                            return 2500;
                        }
                        if (number <= 75) {
                            return 3000;
                        }
                        if (number <= 100) {
                            return 3500;
                        }
                        return 4000;
                    }
                    timeout = estimator(messageText.trim().length);
                } else {
                    timeout = 0;
                }
            }
            // show typing indication
            if (timeout) {
                me.postMessage({
                    event: ChatEvents.OnTypingArrived,
                    value: { indicator: true }
                });
            }
            setTimeout(() => {
                // hide typing indication
                me.postMessage({
                    event: ChatEvents.OnTypingArrived,
                    value: { indicator: false }
                });
                resolve(true);
            }, timeout);
        });
    }

    isLinkSameAsHost(link) {
        try {
            let hrefURL;
            let pageURL;
            hrefURL = new URL(link);
            pageURL = new URL(window.location);
            if (!(link.startsWith('/') || hrefURL.host === pageURL.host)) {
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    }

    openLinkIfExists(data) {
        let linkMatch = data.answers.match && data.answers.match("href='(.*)'");
        if (!linkMatch && data.answers && data.answers[0].option && data.answers[0].option.match) {
            linkMatch = data.answers[0].option.match("href='(.*)'")
        }
        if (linkMatch && linkMatch[1]) {
            if (linkMatch[1] && this.isLinkSameAsHost(linkMatch[1])) {
                window.open(linkMatch[1], '_self');
            } else {
                window.open(linkMatch[1], '_blank');
            }
        }
    }

    publishQuestionTypeResponse(data, cb) {
        const me = this;
        logIt('publishQuestionTypeResponse:');
        logIt(data);
        data.chatId = me.getChatId();
        /// /Send QT response flag with the data
        data.isQTResponseSent = data.questionType > 0;
        me.webSyncClient.publish({
            retries: 1,
            meta: me.getMeta(me.getChatId(), me.options.companyId, me.options.token),
            channel: `/chatroom/${data.chatId}/templatedquestions/answer`,
            data,
            onSuccess() {
                if (cb && typeof cb === 'function') {
                    logIt('FT:: FT/QT processed... do anything in a callback');
                    cb();
                }
                me.openLinkIfExists(data);
            }
        });
    }

    OnSMSTransfer(args) {
        const me = this;
        me.isChatTransferredToSMS = true;
        if (args.systemMessageType == SystemMessageType.SMSTransferInProgress) {
            // if chat transferred to sms unsubscribe chat channels and remove cookies to disabled chat.
            me.postMessage({
                event: WidgetTypes.ChatRoom + ChatEvents.OnSMSChatTransferring,
                data: args
            });
        }
        // uncomment and implement SMSTransferred status when required
        // else if (args.systemMessageType == SystemMessageType.SMSTransferred) {
        //        me.postMessage({
        //            event: WidgetTypes.ChatRoom + ChatEvents.OnSMSChatTransferred,
        //            data: args
        //        });
        //        me.postMessage({
        //            event: WidgetTypes.ChatRoom + ChatEvents.ChatIdleOut
        //        });
        // }
    }

    onParticipantsChanged(participants) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnParticipantsChanged,
            value: participants
        });
    }

    hideWaitingIndicator(participants) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnHideWaitingIndicator,
            value: participants
        });
    }

    onTypingArrived(args, self) {
        if (self) {
            return;
        }
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnTypingArrived,
            value: args
        });
    }

    onCallStatusArrived(args) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnCallStatusArrived,
            value: args
        });
    }

    onChatEnded(args) {
        const me = this;
        me.state.changeState({
            origin: me.objectType,
            toState: ChatEvents.OnChatEnded,
            value: args
        });
    }

    leaveChat() {
        const id = this.options.chatId || this.getChatId();
        const me = this;
        me.webSyncClient.unsubscribe({
            channels: [
                `/chatroom/${id}`,
                `/chatroom/${id}/typing`,
                `/chatroom/${id}/participants`,
                `/chatroom/${id}/meta`,
                `/chatroom/${id}/messagereceipt`
            ],
            onFailure(args) {
                alert(args.getErrorMessage());
            }
        });
    }

    publishPreChatMessages(args) {
        if (isAutoLaunchChatWindow(this.options)) {
            ApexChat.storageAPI.StoreTemp(TempCache.APEXCHAT_PRECHAT_MESSAGE_SENT_COOKIE, true);
            return;
        }
        ApexChat.webSyncClient.publish({
            retries: 0,
            meta: args.meta,
            channel: `/chatroom/${args.chatId}/prechatmessages`,
            data: args.data,
            onSuccess() {
                ApexChat.storageAPI.StoreTemp(TempCache.APEXCHAT_PRECHAT_MESSAGE_SENT_COOKIE, true);
                setTimeout(() => {
                    ApexChat.storageAPI.Remove(TempCache.APEXCHAT_PRECHAT_MESSAGE_IDS_COOKIE);
                }, 6000);
            }
        });
    }

    handleFullTextVisitorResponse(m) {
        ApexChat.chatArray = ApexChat.chatArray || [];
        const chatLogLength = ApexChat.chatArray.length;
        // Bug_7343 - Visitor's responses against FullText not appearing
        // summary: in case of a full text response, we forcefully clear it from being tagged: QuestionTypeAnswer so that we can show the full text response on chat messages
        if (chatLogLength > 0) {
            const previousMsg = ApexChat.chatArray[chatLogLength - 1];
            // We need to make previous message options null only when we have got answer in current message
            if (
                previousMsg &&
                m.options == 'QuestionTypeAnswer' &&
                previousMsg.questionType === ScriptsQuestionType.FullText
            ) {
                m.options = null;
            }
        }
        if (m.participantId != ParticipantIds.pokeMessage) {
            // don't need to be push poke messages in chatArray
            ApexChat.chatArray.push(m);
        }
        return m.options;
    }

    handleVisitorResponsesRenderAsText(m) {
        const me = this;
        ApexChat.chatArray = ApexChat.chatArray || [];
        const chatLogLength = ApexChat.chatArray.length;

        // summary: in case of a MCBottomSheet List response, we forcefully clear it from being tagged: QuestionTypeAnswer so that we can show the full text response on chat messages
        if (chatLogLength > 0) {
            const previousMsg = ApexChat.chatArray[chatLogLength - 2];
            // We need to make previous message options null only when we have got answer in current message
            if (previousMsg && m.options == 'QuestionTypeAnswer') {
                const isATA = previousMsg.questionType == ScriptsQuestionType.AllThatApply;
                const isMC =
                    previousMsg.questionType == ScriptsQuestionType.MultipleChoice;
                const isTF = previousMsg.questionType == ScriptsQuestionType.TrueOrFalse;
                const isSMSOptIn =
                    previousMsg.questionType == ScriptsQuestionType.MultipleChoiceConsent;

                const isText = previousMsg.questionType == ScriptsQuestionType.Text;
                const isDateTimeQT =
                    previousMsg.questionType == ScriptsQuestionType.Time ||
                    previousMsg.questionType == ScriptsQuestionType.TimeCalendar ||
                    previousMsg.questionType == ScriptsQuestionType.CalendarDate;
                const isWebToSMS = previousMsg.questionType == ScriptsQuestionType.WebToSMSNotification
                    || previousMsg.questionType == ScriptsQuestionType.PhoneOTPDialog;
                // BottomSheet & ATA-List Responses
                if (
                    (isATA || isMC) &&
                    previousMsg.questionTypeDisplayListForVisitor &&
                    (me.options.widgetType.indexOf(WidgetTypes.DOM) !== -1 ||
                        me.options.widgetType.indexOf(WidgetTypes.Windowed) !== -1)
                ) {
                    m.options = null;
                }
                // ATA - Non List Responses
                if (isATA && !previousMsg.questionTypeDisplayListForVisitor) {
                    m.options = null;
                }
                // MC - Non List Responses
                if (isMC && !previousMsg.questionTypeDisplayListForVisitor) {
                    m.options = null;
                }
                // TF - Responses
                if (isTF || isDateTimeQT || isText || isSMSOptIn || isWebToSMS) {
                    m.options = null;
                }
                // EP - QT Responses
                if (
                    previousMsg.participantId == ParticipantIds.predefined &&
                    m.options == 'QuestionTypeAnswer' &&
                    previousMsg.userId == UserIds.simulated
                ) {
                    m.options = null;
                }
            }
        }
        return m.options;
    }
}
